<template>
  <v-container fluid pa-0>
    <v-row v-if="routes.length == 0">
      <add-btn
        label="Add New Route"
        @click="onAddRoute"
        icon="mdi-map-marker-plus"
      ></add-btn>
    </v-row>
    <v-row v-else>
      <v-col cols="12">
        <v-container fluid py-0>
          <v-row justify="end" align="center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn small v-on="on" class="ml-2" fab @click="onCreateRoute">
                  <v-icon small>mdi-file-plus-outline</v-icon>
                </v-btn>
              </template>
              Create New Route
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  class="ml-2"
                  fab
                  dark
                  color="primary"
                  @click="onAddRoute"
                >
                  <v-icon dark>mdi-plus</v-icon>
                </v-btn>
              </template>
              Add Route
            </v-tooltip>
          </v-row>
        </v-container>
        <v-row>
          <v-col>
            <v-card outlined>
              <v-data-table :headers="headers" :items="routes">
                <template v-slot:item.actions="{ item }">
                  <default-list-actions
                    :item="item"
                    @deleteItem="deleteItem"
                    @viewItem="viewItem"
                    :hide-view="false"
                    hide-edit
                  ></default-list-actions>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <routes-dialog
      :show="showAddRouteDialog"
      @close="showAddRouteDialog = false"
      @create="onCreate"
    ></routes-dialog>
    <route-details-dialog
      :show="showRouteDetailsDialog"
      @close="showRouteDetailsDialog = false"
      v-model="selectedRoute"
      @save="onRouteSave"
      show-tags
      show-date
      show-sequence
      show-color-picker
    ></route-details-dialog>
  </v-container>
</template>

<script>
import AddBtn from "../../../shared/AddBtn";
import DefaultListActions from "../../../shared/DefaultListActions";
import RouteDetailsDialog from "../../../itineraries/routes/RouteDetailsDialog";
import RoutesDialog from "../../../itineraries/routes/fb/RoutesDialog";
import gql from "graphql-tag";
import EventBus from "../../../../../store/event-bus";

const CREATE_SERVICE_ROUTE = gql`
  mutation createServiceRoute($serviceId: ID!, $route: ServiceRouteInput!) {
    createServiceRoute(serviceId: $serviceId, route: $route) {
      id
      name
      code
      description
      coordinates
      tags
      color
      pois {
        edges {
          node {
            id
            name
            lng
            lat
            description
            radius
            audio
            type {
              id
              name
              iconName
              iconSVG
            }
          }
        }
      }
    }
  }
`;
const UPDATE_SERVICE_ROUTE = gql`
  mutation updateServiceRoute($id: ID!, $route: ServiceRouteInput!) {
    updateServiceRoute(id: $id, route: $route) {
      id
      name
      code
      description
      coordinates
      tags
      color
      date
      sequence
      pois {
        edges {
          node {
            id
            name
            lng
            lat
            description
            radius
            audio
            type {
              id
              name
              iconName
              iconSVG
            }
          }
        }
      }
    }
  }
`;
const DELETE_SERVICE_ROUTE = gql`
  mutation deleteServiceRoute($id: ID!) {
    deleteServiceRoute(id: $id) {
      id
    }
  }
`;

export default {
  name: "service-routes",

  props: {
    value: {
      type: Array,
      default: () => [],
    },
    serviceId: {
      type: String,
      default: () => "",
    },
  },

  components: {
    AddBtn,
    DefaultListActions,
    RouteDetailsDialog,
    RoutesDialog,
  },

  data() {
    return {
      showAddRouteDialog: false,
      showRouteDetailsDialog: false,
      selectedRoute: null,
      routes: this.value && this.value.length > 0 ? [...this.value] : [],
      headers: [
        { text: "Name", value: "name", name: "name" },
        { text: "Code", value: "code", name: "code" },
        { text: "Description", value: "description", name: "description" },
        { text: "Tags", value: "tags", name: "tags" },
        { text: "Sequence", value: "sequence", name: "sequence" },
        { text: "Actions", value: "actions", sortable: false, name: "actions", align: "center" },
      ],
    };
  },

  watch: {
    value(newRoutes) {
      this.routes = [...newRoutes];
    },
  },

  methods: {
    onAddRoute() {
      this.showAddRouteDialog = true;
    },
    onCreateRoute() {
      this.selectedRoute = { name: "New Route", coordinates: "" };
      this.showRouteDetailsDialog = true;
    },
    onRouteSave(payload) {
      if (this.selectedRoute.id) {
        this.$apollo
          .mutate({
            mutation: UPDATE_SERVICE_ROUTE,
            variables: { id: this.selectedRoute.id, route: { ...payload } },
          })
          .then((result) => {
            this.showRouteDetailsDialog = false;
            const res = result.data.updateServiceRoute;
            const editedIndex = this.routes.findIndex((r) => r.id == res.id);
            this.$set(this.routes, editedIndex, {
              ...res,
              pois: res.pois.edges.map((e) => e.node),
            });
            EventBus.$emit("update-tags");
          })
          .catch((error) => console.error(error));
      } else {
        this.createServiceRoute(payload);
      }
    },
    deleteItem(item) {
      this.$apollo
        .mutate({
          mutation: DELETE_SERVICE_ROUTE,
          variables: { id: item.id },
        })
        .then((result) => {
          this.routes = this.routes.filter(
            (r) => r.id != result.data.deleteServiceRoute.id
          );
        })
        .catch((error) => console.error(error));
    },
    viewItem(item) {
      const pois = Array.isArray(item.pois)
        ? { edges: item.pois.map((p) => ({ node: p })) }
        : item.pois || { edges: [] };
      this.selectedRoute = { ...item, pois };
      this.showRouteDetailsDialog = true;
    },
    onCreate(payload) {
      this.showAddRouteDialog = false;
      this.createServiceRoute(payload);
    },
    createServiceRoute(payload) {
      this.$apollo
        .mutate({
          mutation: CREATE_SERVICE_ROUTE,
          variables: {
            serviceId: this.serviceId,
            route: {
              name: payload.name,
              code: payload.code,
              description: payload.description,
              coordinates: payload.coordinates,
              tags: payload.tags,
              color: payload.color,
              pois: payload.pois,
            },
          },
        })
        .then((result) => {
          this.routes.push({ ...result.data.createServiceRoute });
          if (this.showRouteDetailsDialog) {
            this.showRouteDetailsDialog = false;
          }
          EventBus.$emit("update-tags");
        })
        .catch((error) => console.error(error));
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/gt/suppliers/services/routes/ServiceRoutes.vue